import React from 'react';

import {format} from 'date-fns';
import {useGate, useUnit} from 'effector-react';
import {useTranslation} from 'react-i18next';
import {useHistory} from 'react-router-dom';

import {ROUTES} from '@app/routing';
import {EMPTY_VALUE as EMPTY} from '@consts/common';
import {STATUS_COLOR_LIST} from '@consts/statuses';
import {DocumentTemplatesGate, documentTemplatesModel} from '@entities/document-templates/model';
import {
    DictionaryItem,
    LocalizedTitle,
    NotificationTemplateDto,
} from '@entities/document-templates/types';
import {Button, ButtonVariant} from '@shared/ui/Button';
import {StatusWrapper} from '@shared/ui/PageComponents';
import {Select, SelectOption} from '@shared/ui/Select';
import {mapTableColumns, Table} from '@shared/ui/Table2.0';

import {Actions, ControlsCard, FilterBox, Wrapper} from './styled';

const localizeTitle = (title: LocalizedTitle | null | undefined, language: string) => {
    if (!title) return EMPTY;
    return title[language] ?? title.en ?? EMPTY;
};

const formatDate = (value?: string | null) => {
    if (!value) return EMPTY;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return EMPTY;
    return format(date, 'dd.MM.yyyy');
};

const statusColor = (code?: string | null) =>
    code === 'ENABLED' ? STATUS_COLOR_LIST.GREEN : STATUS_COLOR_LIST.GRAY_ALT;

const getColumnsConfig = (
    t: (key: string, params?: Record<string, unknown>) => string,
    violationTypeOptions: Array<SelectOption<string>>,
    kindOptions: Array<SelectOption<string>>,
    statusOptions: Array<SelectOption<string>>,
) => [
    {
        id: 'violationType',
        header: t('page_audit.terms.violation-type'),
        minWidth: 197,
        filter: <Select isMulti options={violationTypeOptions} closeMenuOnSelect={false} />,
    },
    {id: 'code', header: t('page_audit.terms.code'), width: 200},
    {id: 'title', header: t('page_audit.terms.template-name'), minWidth: 400},
    {
        id: 'kind',
        header: t('page_audit.terms.notification-type'),
        minWidth: 160,
        filter: <Select isClearable options={kindOptions} />,
    },
    {
        id: 'status',
        header: t('page_audit.terms.status'),
        width: 200,
        filter: <Select isClearable options={statusOptions} />,
    },
];

export const DocumentTemplatesTab = () => {
    const {t, i18n} = useTranslation();
    const history = useHistory();
    const templates = useUnit(documentTemplatesModel);

    useGate(DocumentTemplatesGate);

    const violationTypeOptions = React.useMemo(
        () =>
            templates.state.violationTypes.map((item) => ({
                label: localizeTitle(item.title, i18n.language),
                value: item.id,
            })),
        [i18n.language, templates.state.violationTypes],
    );

    const kindOptions = React.useMemo(
        () =>
            templates.state.notificationKinds.map((item) => ({
                label: localizeTitle(item.title, i18n.language),
                value: item.code,
            })),
        [i18n.language, templates.state.notificationKinds],
    );

    const statusOptions = React.useMemo(
        () =>
            templates.state.activeStatuses.map((item) => ({
                label: localizeTitle(item.title, i18n.language),
                value: item.code,
            })),
        [i18n.language, templates.state.activeStatuses],
    );

    const data = React.useMemo(
        () =>
            (templates.state.data?.data ?? []).map((row: NotificationTemplateDto) => {
                const violationType = templates.state.violationTypes.find(
                    (item: DictionaryItem) => item.id === row.violationType,
                );
                const status = templates.state.activeStatuses.find(
                    (item: DictionaryItem) => item.id === row.status || item.code === row.status,
                );
                const title = localizeTitle(row.title, i18n.language);

                return {
                    ...row,
                    violationType: localizeTitle(violationType?.title, i18n.language),
                    title: t('page_audit.page_document-templates.document-template-name-from', {
                        title,
                        date: formatDate(row.updated),
                    }),
                    kind: localizeTitle(row.kind?.title, i18n.language),
                    status: (
                        <StatusWrapper color={statusColor(status?.code)}>
                            {localizeTitle(status?.title, i18n.language)}
                        </StatusWrapper>
                    ),
                };
            }),
        [
            i18n.language,
            t,
            templates.state.activeStatuses,
            templates.state.data,
            templates.state.violationTypes,
        ],
    );

    const columns = React.useMemo(
        () =>
            mapTableColumns(
                getColumnsConfig(t, violationTypeOptions, kindOptions, statusOptions),
                templates.state.filters,
                templates.filtersChanged,
                templates.filtersReset,
                templates.state.sortings,
                templates.sortingsChanged,
            ),
        [
            kindOptions,
            statusOptions,
            t,
            templates.filtersChanged,
            templates.filtersReset,
            templates.sortingsChanged,
            templates.state.filters,
            templates.state.sortings,
            violationTypeOptions,
        ],
    );

    return (
        <Wrapper>
            <ControlsCard>
                <FilterBox>
                    <Select
                        isMulti
                        size="sm"
                        options={violationTypeOptions}
                        value={templates.state.filters.violationType}
                        placeholder={t('page_audit.terms.violation-type')}
                        closeMenuOnSelect={false}
                        onChange={(value: unknown) =>
                            templates.filtersChanged([
                                {
                                    id: 'violationType',
                                    value: value as Array<SelectOption<string>>,
                                },
                            ])
                        }
                    />
                </FilterBox>
                <FilterBox>
                    <Select
                        isClearable
                        size="sm"
                        options={kindOptions}
                        value={templates.state.filters.kind}
                        placeholder={t('page_audit.terms.notification-type')}
                        onChange={(value: unknown) =>
                            templates.filtersChanged([
                                {id: 'kind', value: (value as SelectOption<string>) ?? undefined},
                            ])
                        }
                    />
                </FilterBox>
                <Actions>
                    <Button
                        size="sm"
                        variant={ButtonVariant.Outline}
                        onClick={() =>
                            history.push(
                                ROUTES.PREFERENCES_DOCUMENT_TEMPLATE_DETAILS.replace(
                                    ':notificationTemplateId',
                                    'new',
                                ),
                            )
                        }
                    >
                        {t('page_audit.page_document-templates.create-new-template')}
                    </Button>
                </Actions>
            </ControlsCard>

            <Table
                columns={columns}
                data={data}
                isLoading={templates.state.pending}
                pageCount={Math.ceil((templates.state.data?.total ?? 0) / templates.state.pageSize)}
                pageIndex={templates.state.pageNumber}
                totalRowsCount={templates.state.data?.total ?? 0}
                onPageChange={templates.pageNumberChanged}
                onPageSizeChange={templates.pageSizeChanged}
                onRowClick={(row) =>
                    history.push(
                        ROUTES.PREFERENCES_DOCUMENT_TEMPLATE_DETAILS.replace(
                            ':notificationTemplateId',
                            row.original.id,
                        ),
                    )
                }
            />
        </Wrapper>
    );
};
